import { useMemo } from 'react';

import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';

import type { GetOrgYouthsRequest, OrganizationMember } from '@appTypes/esb';
import {
  useGetOrgAdultsQuery,
  useGetOrgYouthsQuery,
} from '@modules/roster/api';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';

import { mergeEnrichment, uniqueMembers } from '../models/rosterMember';
import {
  type RosterMemberView,
  memberViewFromList,
} from '../models/rosterMemberView';
import { useAdvancementEnrichment } from './useAdvancementEnrichment';
import { useSubUnitEnrichment } from './useSubUnitEnrichment';

const EMPTY_MEMBERS: OrganizationMember[] = [];

interface UseOrgRosterOptions {
  /**
   * Include expired registrations in the result. Defaults to `true`; the
   * sub-unit pickers leave them out, since the sub-unit endpoints reject them.
   */
  includeExpired?: boolean;
  /** Include address, phone, and email for each member. Defaults to `true`. */
  includeAddressPhoneEmail?: boolean;
  /** Include registration details for each member. Defaults to `true`. */
  includeRegistrationDetails?: boolean;
  /**
   * Exact request body, used instead of the three `include*` flags, so the
   * query shares the cache entry of a request built elsewhere
   */
  body?: GetOrgYouthsRequest;
  /** Fetch only one side of the roster to avoid an unneeded request. */
  include?: 'all' | 'youths' | 'adults';
  /** Skip all requests (e.g. while a prerequisite is loading). */
  skip?: boolean;
}

/**
 * RTK Query source of truth for the roster `(orgGuid, body)` query is shared
 * and deduped across every component that mounts this hook, with tag-based
 * invalidation handled by `rosterApi`. This hook only adds the bits that were
 * previously copy-pasted into every consumer: org-guid defaulting, request
 * skipping, and the memoized merge into the canonical {@link RosterMember}.
 *
 * @param orgGuid Organization to load. Defaults to the selected organization.
 */
export function useOrgRoster(orgGuid?: string, opts: UseOrgRosterOptions = {}) {
  const selectedGuid = useSelector(organizationGuidSel);
  const organizationGuid = orgGuid ?? selectedGuid;
  const {
    includeExpired = true,
    includeAddressPhoneEmail = true,
    includeRegistrationDetails = true,
    include = 'all',
    skip = false,
  } = opts;

  const skipYouths = skip || !organizationGuid || include === 'adults';
  const skipAdults = skip || !organizationGuid || include === 'youths';
  const flagsBody = useMemo(
    () => ({
      includeExpired,
      includeAddressPhoneEmail,
      includeRegistrationDetails,
    }),
    [includeExpired, includeAddressPhoneEmail, includeRegistrationDetails],
  );
  const body = opts.body ?? flagsBody;

  const youthQuery = useGetOrgYouthsQuery(
    { organizationGuid: organizationGuid ?? '', body },
    { skip: skipYouths },
  );
  const adultQuery = useGetOrgAdultsQuery(
    { organizationGuid: organizationGuid ?? '', body },
    { skip: skipAdults },
  );

  const youths = youthQuery.data?.members ?? EMPTY_MEMBERS;
  const adults = adultQuery.data?.members ?? EMPTY_MEMBERS;

  const advancementByGuid = useAdvancementEnrichment(organizationGuid ?? '');
  const subUnitEnrichment = useSubUnitEnrichment(organizationGuid ?? '', {
    skipYouths,
    skipAdults,
  });

  const members = useMemo<RosterMemberView[]>(
    () =>
      memberViewFromList(
        mergeEnrichment(
          mergeEnrichment(
            uniqueMembers(youths, adults),
            'advancement',
            advancementByGuid,
          ),
          'subUnit',
          subUnitEnrichment.byGuid,
        ),
      ),
    [youths, adults, advancementByGuid, subUnitEnrichment.byGuid],
  );

  return {
    youths,
    adults,
    members,
    isLoading:
      (!skipYouths && youthQuery.isLoading) ||
      (!skipAdults && adultQuery.isLoading),
    isFetching: youthQuery.isFetching || adultQuery.isFetching,
    subUnitLoading: {
      youths: subUnitEnrichment.isYouthLoading,
      adults: subUnitEnrichment.isAdultLoading,
    },
    subUnitError: subUnitEnrichment.error,
    error: (youthQuery.error ?? adultQuery.error) as
      | FetchBaseQueryError
      | undefined,
    refetch: () => {
      if (!skipYouths) youthQuery.refetch();
      if (!skipAdults) adultQuery.refetch();
      subUnitEnrichment.refetch();
    },
  };
}
